import type { Pet, User } from '../types'

const toPositiveInteger = (value: unknown): number | null => {
  const numberValue = Number(value)
  return Number.isInteger(numberValue) && numberValue > 0 ? numberValue : null
}

export const parseVetFilterFromSearch = (search: string): number | null => {
  const params = new URLSearchParams(search)
  const filter = params.get('filter')
  if (!filter) return null

  try {
    const parsed = JSON.parse(filter)
    return toPositiveInteger(parsed?.vet)
  } catch {
    return null
  }
}

export const getOwnerFromPet = (pet: Pet | any): User | null => {
  const owner = pet?.owner
  return owner && typeof owner === 'object' ? owner : null
}

export const ownerMatchesVetClinic = (
  owner: User | number | null | undefined,
  vetId: number | null,
): boolean => {
  if (!vetId || owner === null || owner === undefined) return false

  if (typeof owner === 'number') {
    return owner === vetId
  }

  if (owner.id === vetId) {
    return true
  }

  if (!Array.isArray(owner.vets)) {
    return false
  }

  return owner.vets.some((vet) => {
    if (typeof vet === 'number') return vet === vetId
    return vet?.id === vetId
  })
}

export const petMatchesVetClinic = (pet: Pet | any, vetId: number | null): boolean => {
  if (!vetId) return true
  return ownerMatchesVetClinic(pet?.owner, vetId)
}

/**
 * The Clinic Team "View Patients" filter — `#/pets?filter={"assignedVet":N}`.
 *
 * Deliberately a SEPARATE key from the Clinic Directory's `vet` filter, and it
 * reads a different field. `vet` asks "who can see this pet" (`owner.vets`, the
 * access substrate); `assignedVet` asks "who is responsible for it"
 * (`pets.assigned_vet_id`). A sub-account is normally absent from `owner.vets`
 * altogether — PRD-MASTER-SUB-ACCOUNT §11.7 — so routing this through
 * petMatchesVetClinic would return an empty list for every sub whether or not
 * it had patients assigned, which is precisely the false signal this filter
 * exists to remove.
 */
export const parseAssignedVetFilterFromSearch = (search: string): number | null => {
  const params = new URLSearchParams(search)
  const filter = params.get('filter')
  if (!filter) return null

  try {
    const parsed = JSON.parse(filter)
    return toPositiveInteger(parsed?.assignedVet)
  } catch {
    return null
  }
}

/**
 * Every assignee id of a pet. `assignedVetIds` is authoritative; the legacy
 * single `assignedVet` (a populated User or a bare id) is read only when the
 * list is absent (an older backend, a cached record).
 */
export const petAssigneeIds = (pet: Pet | any): number[] => {
  const ids = pet?.assignedVetIds
  if (Array.isArray(ids)) {
    return ids.map(Number).filter((n) => Number.isInteger(n) && n > 0)
  }
  const assigned = pet?.assignedVet
  if (assigned === null || assigned === undefined) return []
  const id = Number(typeof assigned === 'object' ? assigned?.id : assigned)
  return Number.isInteger(id) && id > 0 ? [id] : []
}

/**
 * Is `vetId` ONE OF this pet's assignees (vic/vetpo#70)? A patient with two
 * vets matches both of their "View Patients" links.
 *
 * An UNASSIGNED pet matches nobody. That is not a degenerate case: prod carried
 * 0 of 1142 active pets with an assignment on 2026-08-21, so it is the branch
 * every real click takes today, and an empty list is the honest answer.
 */
export const petAssignedToVet = (pet: Pet | any, vetId: number | null): boolean => {
  if (!vetId) return false
  return petAssigneeIds(pet).includes(vetId)
}
